import { Alert, Box, Typography } from '@mui/material';
import { PageContainer } from '@toolpad/core/PageContainer';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ActivityDashboard from '../components/Admin/ActivityDashboard';
import AdminOverviewPanel from '../components/Admin/AdminOverviewPanel';
import BillingManagementPanel from '../components/Admin/BillingManagementPanel';
import ClientSettingsPanel from '../components/Admin/ClientSettingsPanel';
import HelpdeskAdminPanel from '../components/Admin/HelpdeskAdminPanel';
import ProcurementClientSettingsPanel from '../components/Admin/ProcurementClientSettingsPanel';
import StoragePanel from '../components/Admin/Storage/StoragePanel';
import TeamManagementPanel from '../components/Admin/TeamManagement/TeamManagementPanel';
import UserManagementPanel from '../components/Admin/UserManagementPanel';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { PERMISSIONS } from '../shared/constants/permissions';
import { ACTIVITY_TYPES, logActivity } from '../utils/activityLogger';
import LibraryPage from './LibraryPage';

const AdminPage = () => {
  const [searchParams] = useSearchParams();
  const { tab: paramTab } = useParams<{ tab?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, isLoading } = useAuth();
  const { account, refreshUserDetails, hasPermission } = useAccount();
  const { mode } = useWorkspace();
  const [error, setError] = useState(null);

  // Determine active tab from URL path or search params, defaulting to 'overview'
  const activeTab = paramTab || searchParams.get('tab') || 'overview';

  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
    }
  }, [location.state]);

  useEffect(() => {
    if (searchParams.get('sharepoint_connected') === 'true') {
      refreshUserDetails();
    }
  }, [searchParams, refreshUserDetails]);

  // Permission Checks
  const canViewAnalytics = React.useCallback(
    () => hasPermission(PERMISSIONS.INSIGHTS_VIEW),
    [hasPermission]
  );
  const canViewHelpdesk = React.useCallback(
    () => hasPermission(PERMISSIONS.TICKET_MANAGE),
    [hasPermission]
  );
  const canManageTeams = React.useCallback(
    () => hasPermission(PERMISSIONS.TEAM_VIEW),
    [hasPermission]
  );
  const canManageUsers = React.useCallback(
    () => hasPermission(PERMISSIONS.USER_VIEW),
    [hasPermission]
  );
  const canManageBilling = React.useCallback(
    () => hasPermission(PERMISSIONS.BILLING_MANAGE),
    [hasPermission]
  );
  const canManageSettings = React.useCallback(
    () => hasPermission(PERMISSIONS.SETTINGS_MANAGE),
    [hasPermission]
  );
  const canManageLibrary = React.useCallback(
    () => hasPermission(PERMISSIONS.LIBRARY_MANAGE_CLIENT),
    [hasPermission]
  );

  // Check if user has any access to the admin panel
  const hasAdminAccess = React.useCallback(() => {
    if (!account) {
      return false;
    }
    return (
      canViewAnalytics() ||
      canViewHelpdesk() ||
      canManageTeams() ||
      canManageUsers() ||
      canManageBilling() ||
      canManageSettings() ||
      canManageLibrary()
    );
  }, [
    account,
    canViewAnalytics,
    canViewHelpdesk,
    canManageTeams,
    canManageUsers,
    canManageBilling,
    canManageSettings,
    canManageLibrary,
  ]);

  useEffect(() => {
    if (isLoading) {
      return; // Wait for auth to be resolved
    }

    const logAndSignOut = async (reason: string) => {
      if (account) {
        await logActivity(ACTIVITY_TYPES.SECURITY_EVENT, {
          eventType: 'unauthorized_access_attempt',
          resource: `/admin?tab=${activeTab}`,
          details: `User ${account.email} with role ${account.role} attempted to access a restricted area. Reason: ${reason}`,
        });
      }
      await logout();
      navigate('/login', { state: { error: 'Access Denied. You have been logged out.' } });
    };

    if (account) {
      // General access check for the admin page
      if (!hasAdminAccess()) {
        logAndSignOut('User does not have necessary permissions.');
        return;
      }

      if (!paramTab) {
        if (searchParams.get('tab')) {
          navigate(`/admin/${searchParams.get('tab')}`, { replace: true });
          return;
        }

        navigate('/admin/overview', { replace: true });
      } else {
        // Handle direct navigation to restricted tabs
        if (activeTab === 'billing' && !canManageBilling()) {
          if (canViewAnalytics()) {
            navigate('/admin/analytics', { replace: true });
          } else if (canViewHelpdesk()) {
            navigate('/admin/helpdesk', { replace: true });
          } else if (canManageTeams()) {
            navigate('/admin/teams', { replace: true });
          } else if (canManageUsers()) {
            navigate('/admin/users', { replace: true });
          }
        }
      }
    }
  }, [
    account,
    navigate,
    isLoading,
    activeTab,
    hasAdminAccess,
    canViewAnalytics,
    canViewHelpdesk,
    canManageTeams,
    canManageUsers,
    canManageBilling,
    paramTab,
    searchParams.get,
    logout,
  ]);

  if (isLoading) {
    return (
      <PageContainer title="Admin Panel" maxWidth={false}>
        <Typography>Loading...</Typography>
      </PageContainer>
    );
  }

  // If no admin access, show access denied
  if (!hasAdminAccess()) {
    return (
      <PageContainer title="Admin Panel" maxWidth={false}>
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography>
            You don't have permission to access the admin panel. Your attempt has been logged and
            you will be signed out.
          </Typography>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="" data-testid="admin-page" maxWidth={false}>
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tab Content */}
        {activeTab === 'overview' && <AdminOverviewPanel />}

        {activeTab === 'helpdesk' && <HelpdeskAdminPanel />}

        {activeTab === 'billing' && canManageBilling() && <BillingManagementPanel />}

        {activeTab === 'teams' && canManageTeams() && (
          <TeamManagementPanel
            maxTeams={
              account?.subscription?.limits?.numTeams ??
              ((account as unknown as Record<string, unknown>)?.maxTeams as number | undefined) ??
              1
            }
          />
        )}

        {activeTab === 'users' && canManageUsers() && <UserManagementPanel />}

        {activeTab === 'knowledge_base' && canManageLibrary() && <LibraryPage />}

        {activeTab === 'storage' && canManageSettings() && <StoragePanel />}

        {activeTab === 'analytics' && canViewAnalytics() && <ActivityDashboard />}

        {activeTab === 'settings' &&
          canManageSettings() &&
          (mode === 'PROCURE' ? <ProcurementClientSettingsPanel /> : <ClientSettingsPanel />)}
      </Box>
    </PageContainer>
  );
};

export default AdminPage;
